import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { User } from '@/types'
import { userService } from '@/services'
import { LoadingState, PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { UserForm } from '@/components/users/UserForm'
import { type UserEditFormData } from '@/lib/validations/user'

export default function UserEditPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  useEffect(() => {
    const loadUser = async () => {
      if (!userId) return

      try {
        const userData = await userService.getById(userId)
        if (userData) {
          setUser(userData)
        }
      } catch (error) {
        console.error('Error loading user:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUser()
  }, [userId])

  const onSubmit = async (data: UserEditFormData) => {
    if (!userId) return

    setIsSaving(true)
    try {
      await userService.updateProfile(userId, data)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Profile updated successfully')
      navigate(`/users/${userId}`)
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return <LoadingState variant="spinner" text="Loading user..." />
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="User Not Found"
          backButton={{
            label: 'Back',
            onClick: () => navigate(-1),
          }}
        />
        <p className="text-muted-foreground">User not found</p>
      </div>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Edit Profile" description="Update user information" />

      <UserForm
        mode="edit"
        user={user}
        onSubmit={onSubmit}
        onCancel={() => navigate(-1)}
        loading={isSaving}
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
